import { useLocation } from 'react-router-dom';

interface SchemaOrgProps {
  type: 'WebSite' | 'Organization' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  data?: Record<string, unknown>;
}

function buildBreadcrumbSchema(pathname: string) {
  const parts = pathname.split('/').filter(Boolean);
  const items = [{ '@type': 'ListItem' as const, position: 1, item: { '@id': 'https://91n.com/', name: '首页' } }];

  if (parts[0] === 'category' && parts[1]) {
    const catMap: Record<string, string> = {
      'register-login': '注册登录',
      'feature-guide': '功能使用',
      faq: '常见问题',
      announcement: '平台公告',
      security: '安全指南',
    };
    items.push({
      '@type': 'ListItem' as const,
      position: 2,
      item: { '@id': `https://91n.com/category/${parts[1]}`, name: catMap[parts[1]] || parts[1] },
    });
  }

  if (parts[0] === 'article' && parts[1]) {
    items.push({
      '@type': 'ListItem' as const,
      position: 2,
      item: { '@id': `https://91n.com/article/${parts[1]}`, name: '文章详情' },
    });
  }

  if (parts[0] === 'about') {
    items.push({
      '@type': 'ListItem' as const,
      position: 2,
      item: { '@id': 'https://91n.com/about', name: '关于我们' },
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  const location = useLocation();

  const getSchema = (): Record<string, unknown> => {
    switch (type) {
      case 'Organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '91n.com',
          url: 'https://91n.com',
          logo: 'https://91n.com/logo.png',
          description: '91n.com 是值得信赖的网络综合服务平台，提供官方入口、核心功能指南、使用教程及常见问题解答。',
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'support@91n.com',
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
          sameAs: ['https://91n.com'],
        };
      case 'WebSite':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '91n.com',
          url: 'https://91n.com',
          description: '91n.com - 官方入口与核心功能全解析，帮助用户快速找到91n.com并充分利用其全部服务。',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: 'https://91n.com/search?q={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
          },
        };
      case 'WebPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          ...(data || {}),
          url: `https://91n.com${location.pathname}`,
          isPartOf: { '@type': 'WebSite', '@id': 'https://91n.com/#website' },
          about: { '@type': 'Organization', '@id': 'https://91n.com/#organization' },
        };
      case 'BreadcrumbList':
        return buildBreadcrumbSchema(location.pathname);
      case 'Article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          ...(data || {}),
        };
      case 'FAQPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          ...(data || {}),
        };
      default:
        return {};
    }
  };

  const schema = getSchema();

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}